import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Assignment as ReportIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import { RootState } from '../../store';
import {
  fetchNCRsStart,
  deleteNCRStart,
  setSearchTerm,
  setStatusFilter,
  setWorkOrderFilter,
  setDateFromFilter,
  setDateToFilter,
  setPage,
  setPageSize,
  clearError,
  setDetailModalOpen,
  setDeleteDialogOpen,
  setNCRToDelete,
  generateReportStart,
} from '../../store/reducers/ncrSlice';
import { NCR } from '../../services/ncrApi';
import { ncrApi } from '../../services/ncrApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 'bold',
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  summaryCard: {
    textAlign: 'center',
  },
  summaryNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterField: {
    minWidth: 200,
  },
  tableContainer: {
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    fontWeight: 'bold',
  },
  statusOpen: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  statusClosed: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  actionButton: {
    marginLeft: theme.spacing(0.5),
  },
  noData: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

const NCRList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    ncrs,
    loading,
    error,
    pagination,
    searchTerm,
    statusFilter,
    workOrderFilter,
    dateFromFilter,
    dateToFilter,
    detailModalOpen,
    deleteDialogOpen,
    ncrToDelete,
    statistics,
    dropdowns,
  } = useSelector((state: RootState) => state.ncr);

  const [selectedNCR, setSelectedNCR] = useState<NCR | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchNCRsStart());
  }, [dispatch]);

  // Handlers
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(setStatusFilter(event.target.value as 'All' | 'Open' | 'Closed'));
  };

  const handleWorkOrderFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(setWorkOrderFilter(event.target.value as string));
  };

  const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setDateFromFilter(event.target.value));
  };

  const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setDateToFilter(event.target.value));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPageSize(parseInt(event.target.value, 10)));
  };

  const handleRefresh = () => {
    dispatch(fetchNCRsStart());
  };

  const handleAddNew = () => {
    navigate('/reports/ncr/add');
  };

  const handleViewNCR = (ncr: NCR) => {
    setSelectedNCR(ncr);
    dispatch(setDetailModalOpen(true));
  };

  const handleFillNCR = (ncr: NCR) => {
    navigate(`/reports/ncr/fill/${ncr.id}`);
  };

  const handleEditNCR = (ncr: NCR) => {
    navigate(`/reports/ncr/edit/${ncr.id}`);
  };

  const handleDeleteNCR = (ncr: NCR) => {
    dispatch(setNCRToDelete(ncr));
    dispatch(setDeleteDialogOpen(true));
  };

  const handleConfirmDelete = () => {
    if (ncrToDelete) {
      dispatch(deleteNCRStart(ncrToDelete.id));
      dispatch(setDeleteDialogOpen(false));
    }
  };

  const handleCancelDelete = () => {
    dispatch(setDeleteDialogOpen(false));
  };

  const handlePrintReport = async (ncr: NCR) => {
    try {
      const blob = await ncrApi.printReport(ncr.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NCR-${ncr.ncr_folio_num}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const csvContent = ncrApi.exportToCSV(ncrs);
      ncrApi.downloadCSV(csvContent, `NCRs-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleCloseDetailModal = () => {
    dispatch(setDetailModalOpen(false));
    setSelectedNCR(null);
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  // Utility functions
  const getStatusChip = (status: 'Open' | 'Closed') => (
    <Chip
      label={status}
      className={`${classes.statusChip} ${
        status === 'Open' ? classes.statusOpen : classes.statusClosed
      }`}
      size="small"
    />
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && (!ncrs || ncrs.length === 0)) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Non-Conformance Reports (NCR)
        </Typography>
        <Box className={classes.headerActions}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to CSV">
            <IconButton onClick={handleExportCSV} disabled={exportLoading}>
              <ExportIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Create New NCR
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box className={classes.summaryCards}>
        <Card className={classes.summaryCard}>
          <CardContent>
            <Typography className={classes.summaryNumber}>
              {statistics.totalOpen}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Open NCRs
            </Typography>
          </CardContent>
        </Card>
        <Card className={classes.summaryCard}>
          <CardContent>
            <Typography className={classes.summaryNumber}>
              {statistics.totalClosed}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Closed NCRs
            </Typography>
          </CardContent>
        </Card>
        <Card className={classes.summaryCard}>
          <CardContent>
            <Typography className={classes.summaryNumber}>
              {statistics.totalThisMonth}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              This Month
            </Typography>
          </CardContent>
        </Card>
        <Card className={classes.summaryCard}>
          <CardContent>
            <Typography className={classes.summaryNumber}>
              {statistics.averageCloseTime}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Avg. Close Time (Days)
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Box className={classes.filtersContainer}>
        <TextField
          className={classes.filterField}
          label="Search NCR Folio"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <SearchIcon color="action" />,
          }}
        />
        <FormControl variant="outlined" size="small" className={classes.filterField}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status">
            <MenuItem value="All">All Status</MenuItem>
            <MenuItem value="Open">Open</MenuItem>
            <MenuItem value="Closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterField}>
          <InputLabel>Work Order</InputLabel>
          <Select value={workOrderFilter} onChange={handleWorkOrderFilterChange} label="Work Order">
            <MenuItem value="">All Work Orders</MenuItem>
            {dropdowns.workOrders.map((wo) => (
              <MenuItem key={wo.id} value={wo.id}>
                {wo.wo_num}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          className={classes.filterField}
          label="Date From"
          type="date"
          size="small"
          variant="outlined"
          value={dateFromFilter}
          onChange={handleDateFromChange}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          className={classes.filterField}
          label="Date To"
          type="date"
          size="small"
          variant="outlined"
          value={dateToFilter}
          onChange={handleDateToChange}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* NCR Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>NCR Folio</TableCell>
              <TableCell>Work Order</TableCell>
              <TableCell>Operation</TableCell>
              <TableCell>Part Info</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Concession No.</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (!ncrs || ncrs.length === 0) ? (
              <TableRow>
                <TableCell colSpan={8} className={classes.noData}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    Loading NCRs...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (!ncrs || ncrs.length === 0) ? (
              <TableRow>
                <TableCell colSpan={8} className={classes.noData}>
                  {searchTerm || statusFilter !== 'All' || workOrderFilter || dateFromFilter || dateToFilter
                    ? 'No NCRs found matching your search criteria.'
                    : 'No NCRs found. Click "Create New NCR" to create your first non-conformance report.'}
                </TableCell>
              </TableRow>
            ) : (
              ncrs.map((ncr) => (
                <TableRow key={ncr.id} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {ncr.ncr_folio_num}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {ncr.work_order?.wo_num || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {ncr.operation?.operation_num || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewNCR(ncr)}
                    >
                      Show Details
                    </Button>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(ncr.closure_status)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {ncr.applicable_concession || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(ncr.created_date)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Fill Report">
                      <IconButton
                        size="small"
                        onClick={() => handleFillNCR(ncr)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print Report">
                      <IconButton
                        size="small"
                        onClick={() => handlePrintReport(ncr)}
                        color="default"
                      >
                        <PrintIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Report">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteNCR(ncr)}
                        style={{ color: '#f44336' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={pagination.totalCount}
        rowsPerPage={pagination.pageSize}
        page={pagination.currentPage - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />

      {/* Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>NCR Details</DialogTitle>
        <DialogContent>
          {selectedNCR && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedNCR.ncr_folio_num}
              </Typography>
              <Typography variant="body2">
                <strong>Work Order:</strong> {selectedNCR.work_order?.wo_num || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Part:</strong> {selectedNCR.part?.number} - {selectedNCR.part?.description || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {selectedNCR.closure_status}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(selectedNCR.created_date)}
              </Typography>
              {selectedNCR.serials && selectedNCR.serials.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2">Serial Details:</Typography>
                  {selectedNCR.serials.map((serial, index) => (
                    <Typography key={index} variant="body2">
                      • {serial.serial_num} - {serial.comments} - {serial.qn_num} - {serial.status?.name}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete NCR "{ncrToDelete?.ncr_folio_num}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={error}
      />
    </Box>
  );
};

export default NCRList; 